<script setup lang="ts">
import { DateTime } from "luxon";
import { ref, onMounted, computed, watch } from "vue";
import { FirestoreService } from "../services/firestore";
import { PaymentSummary } from "../types/payment-summary";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from "@heroicons/vue/20/solid";
import { useLoading } from "../composables/useLoading";
import { useNotification } from "../composables/useNotification";
import CustomSelect from "../components/Common/CustomSelect.vue";
import { useCurrentUser } from "vuefire";
import { UserStore, useStores } from "../composables/useStores";
import StatsIcon from "../assets/images/stats.png";
import JsonCsv from "vue-json-csv";
const { setText, toggleLoading } = useLoading();
const { setError } = useNotification();
const { currentStore, getCurrentStore } = useStores();
const currentYear = ref(DateTime.now().toFormat("yyyy"));
const currentMonth = ref(DateTime.now().toFormat("MM"));
const currentYearMonth = computed(
  () => `${currentYear.value}-${currentMonth.value}`,
);
const user = useCurrentUser();
const paymentSummaries = ref<PaymentSummary[]>([]);
const years = ref<string[]>([]);
const months = ref<string[]>([]);
// Per-day deduction lists (attributed to the day before transfer) for the popup.
const deductionsByDate = ref<Record<string, any[]>>({});
const viewingDeductions = ref<{ date: string; items: any[] } | null>(null);

// Franchise fee column: built but hidden (and excluded from Net) until the
// franchise fee is confirmed for merchant-facing display. Turning it on also
// needs a Firestore read rule for `franchise-fees` for the store's users.
const SHOW_FRANCHISE_FEE = false;

const money = (cents: number) =>
  (cents / 100).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });
const openDeductions = (date: string) => {
  viewingDeductions.value = { date, items: deductionsByDate.value[date] ?? [] };
};

const refresh = async (store: UserStore) => {
  setText("Loading payments...");
  toggleLoading(true);
  try {
    const paymentsPath = `/users/${store.user}/merchants/${store.merchant}/stores/${store.store}/months/${currentYearMonth.value}/payments-summary`;

    const ps = await FirestoreService.getCollection<PaymentSummary>(
      paymentsPath,
    );

    //sort ps by date and find missing date and filling PaymentSummary with 0 values for missing dates
    ps.sort((a, b) => (a.date > b.date ? 1 : -1));
    console.log("before length", ps.length);
    const startDate = DateTime.fromFormat(
      `${currentYearMonth.value}-01`,
      "yyyy-MM-dd",
    );
    //end date should be the largest date in ps so far
    const endDate = ps.length
      ? DateTime.fromFormat(
          `${currentYearMonth.value}-${ps[ps.length - 1].date.split("-")[2]}`,
          "yyyy-MM-dd",
        )
      : startDate;
    const allDates = Array.from({ length: endDate.day }, (_, i) =>
      startDate.plus({ days: i }).toFormat("yyyy-MM-dd"),
    );
    for (const date of allDates) {
      if (!ps.find((p) => p.date === date)) {
        ps.push({
          date,
          total: 0,
          net: 0,
          surcharge: 0,
          commission: 0,
          $self: ps[0].$self?.replace(ps[0].date, date) ?? "",
        } as PaymentSummary);
      }
    }
    console.log("after length", ps.length);
    let temp: PaymentSummary[] = [];
    for (const record of ps) {
      if (!record.$self) continue;

      const refunds = await FirestoreService.getCollection<any>(
        `/${record.$self.replace("fs:", "")}/refunds`,
      );
      let refundsTotal = 0;
      for (const refund of refunds) {
        refundsTotal = refundsTotal + (refund.amount?.value ?? 0);
      }

      const chargebacks = await FirestoreService.getCollection<any>(
        `/${record.$self.replace("fs:", "")}/chargebacks`,
      );
      let chargebacksTotal = 0;
      for (const chargeback of chargebacks) {
        chargebacksTotal = chargebacksTotal + (chargeback.amount?.value ?? 0);
      }
      // Fixed $28 fee per chargeback.
      const chargebackFeesTotal =
        chargebacks.length * PaymentSummary.CHARGEBACK_FEE;

      let franchiseFee: number | undefined;
      if (SHOW_FRANCHISE_FEE) {
        try {
          const storeKey = String(store.store).replace(/^store\./, "");
          const fee = await FirestoreService.getDocument<any>(
            `franchise-fees/${storeKey}_${record.date}`,
          );
          franchiseFee = fee?.status === "transferred" ? fee.amount : undefined;
        } catch (e) {
          console.error("Failed to load franchise fee", e);
        }
      }

      temp = [
        ...temp,
        {
          ...record,
          refundsTotal,
          chargebacksTotal,
          chargebackFeesTotal,
          deductionsTotal: 0,
          franchiseFee,
        },
      ];
    }

    // A transferred deduction comes out of the payout for the day BEFORE it was
    // transferred, so it is shown under executedDate - 1. Read this month's and
    // last month's buckets (a deduction created last month can transfer now).
    const ym = currentYearMonth.value;
    const prevYm = DateTime.fromFormat(`${ym}-01`, "yyyy-MM-dd")
      .minus({ months: 1 })
      .toFormat("yyyy-MM");
    const dedBase = `/users/${store.user}/merchants/${store.merchant}/stores/${store.store}/months`;
    const [curDed, prevDed] = await Promise.all([
      FirestoreService.getCollection<any>(`${dedBase}/${ym}/deductions`),
      FirestoreService.getCollection<any>(`${dedBase}/${prevYm}/deductions`),
    ]);

    const seen = new Set<string>();
    const grouped: Record<string, any[]> = {};
    for (const d of [...curDed, ...prevDed]) {
      const key = d.$self ?? d.id;
      if (key) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      if (d.status !== "transferred" || !d.executedDate) continue;
      const attributed = DateTime.fromISO(d.executedDate)
        .minus({ days: 1 })
        .toFormat("yyyy-MM-dd");
      if (!attributed.startsWith(ym)) continue; // belongs to another month
      (grouped[attributed] = grouped[attributed] ?? []).push(d);
    }

    // Show a row for any day that has deductions but no payments (net negative).
    for (const date of Object.keys(grouped)) {
      if (!temp.find((t) => t.date === date)) {
        temp.push({
          date,
          total: 0,
          net: 0,
          surcharge: 0,
          commission: 0,
          refundsTotal: 0,
          $self: temp[0]?.$self?.replace(temp[0].date, date) ?? "",
        } as PaymentSummary);
      }
    }
    for (const t of temp) {
      t.deductionsTotal = (grouped[t.date] ?? []).reduce(
        (acc, d) => acc + (d.amount?.amount ?? 0),
        0,
      );
    }

    deductionsByDate.value = grouped;
    paymentSummaries.value = temp;
    // paymentSummaries.value =
    //   await FirestoreService.getCollection<PaymentSummary>(path);
  } catch (e) {
    setError("failed to load payments please try again");
  } finally {
    toggleLoading(false);
  }
};
watch(
  [currentYearMonth, currentStore],
  async () => {
    if (!currentYearMonth.value) return;
    if (!user.value) return;
    if (!currentStore.value) return;
    await refresh(currentStore.value);
  },
  { immediate: true },
);

onMounted(() => {
  years.value = Array.from({ length: 5 }, (_, i) =>
    DateTime.now().minus({ years: i }).toFormat("yyyy"),
  );
  months.value = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  getCurrentStore();
});
const nextMonth = () => {
  const current = DateTime.fromFormat(currentYearMonth.value, "yyyy-MM").plus({
    months: 1,
  });
  currentYear.value = current.toFormat("yyyy");
  currentMonth.value = current.toFormat("MM");
};

const previousMonth = () => {
  const current = DateTime.fromFormat(currentYearMonth.value, "yyyy-MM").minus({
    months: 1,
  });
  currentYear.value = current.toFormat("yyyy");
  currentMonth.value = current.toFormat("MM");
};
const total = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + (summary.total ?? 0),
    0,
  ),
);
const commission = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + (summary.commission ?? 0),
    0,
  ),
);
const refunds = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + (PaymentSummary.refund(summary) ?? 0),
    0,
  ),
);
const chargebacks = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + (PaymentSummary.chargebackTotal(summary) ?? 0),
    0,
  ),
);
const deductions = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + PaymentSummary.deductions(summary),
    0,
  ),
);
const net = computed(() =>
  paymentSummaries.value.reduce(
    (acc, summary) => acc + (PaymentSummary.net(summary) ?? 0),
    0,
  ),
);
const stats = computed(() => [
  {
    id: 1,
    name: "Total",
    stat: (total.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
    // action: viewTodayPayments,
  },
  {
    id: 3,
    name: "Fees",
    stat: (commission.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
    // action: viewTodayPayments,
  },
  {
    id: 3,
    name: "Refunds",
    stat: (refunds.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
    // action: viewTodayPayments,
  },
  {
    id: 7,
    name: "Chargebacks",
    stat: (chargebacks.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
  },
  {
    id: 6,
    name: "Deductions",
    stat: (deductions.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
  },
  {
    id: 4,
    name: "Net",
    stat: (net.value / 100).toLocaleString("en-AU", {
      style: "currency",
      currency: "AUD",
    }),
    stat1: "AUD",
    change: "3%",
    changeType: "increase",
    // action: viewTodayPayments,
  },
]);
const monthMapping = {
  January: "01",
  February: "02",
  March: "03",
  April: "04",
  May: "05",
  June: "06",
  July: "07",
  August: "08",
  September: "09",
  October: "10",
  November: "11",
  December: "12",
};
const selectedMonth = ref<keyof typeof monthMapping>(
  DateTime.now().toFormat("LLLL") as keyof typeof monthMapping,
);
watch(selectedMonth, (selectedMonth) => {
  currentMonth.value = monthMapping[selectedMonth];
});

const downloadable = computed(() => {
  if (!paymentSummaries.value) return;
  if (paymentSummaries.value.length === 0) return;
  return JSON.stringify(
    paymentSummaries.value.map((payment) => {
      return {
        Date: payment.date,
        Total: (payment.total ?? 0) / 100,
        Fees: (payment.commission ?? 0) / 100,
        Refunds: (PaymentSummary.refund(payment) ?? 0) / 100,
        Chargebacks: (PaymentSummary.chargeback(payment) ?? 0) / 100,
        "Chargeback Fees": (PaymentSummary.chargebackFee(payment) ?? 0) / 100,
        Deductions: PaymentSummary.deductions(payment) / 100,
        Net: (PaymentSummary.net(payment) ?? 0) / 100,
      };
    }),
  );
});
</script>
<template>
  <div class="px-6 pb-8">
    <div class="flex flex-row justify-start items-center space-x-2">
      <InformationCircleIcon class="w-5 h-5 text-gray-400" />
      <p class="text-sm text-gray-400">
        Actual amount may differ due to refunds and chargebacks
      </p>
    </div>
    <div class="mb-6">
      <dl class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6">
        <div
          v-for="item in stats"
          :key="item.id"
          class="relative overflow-hidden rounded-lg bg-white p-6 shadow"
        >
          <div>
            <img :src="StatsIcon" class="w-[56px]" />
          </div>

          <!-- <p
            :class="[
              item.changeType === 'increase'
                ? 'text-green-600'
                : 'text-red-600',
              'ml-2 flex items-baseline text-sm font-semibold',
            ]"
          >
            <ArrowUpIcon
              v-if="item.changeType === 'increase'"
              class="h-5 w-5 flex-shrink-0 self-center text-green-500"
              aria-hidden="true"
            />
            <ArrowDownIcon
              v-else
              class="h-5 w-5 flex-shrink-0 self-center text-red-500"
              aria-hidden="true"
            />
            <span class="sr-only">
              {{ item.changeType === "increase" ? "Increased" : "Decreased" }}
              by
            </span>
            {{ item.change }}
          </p> -->
          <div class="flex flex-row justify-start items-center space-x-2 mt-4">
            <p class="text-3xl font-medium font-poppins text-[#111827]">
              {{ item.stat }}
            </p>

            <!-- <tag
              :label="item.tag"
              color="green"
              v-if="item.tag && item.tagCondition && item.tagCondition()"
            /> -->
          </div>

          <!-- <div
            class="flex flex-row justify-start items-center space-x-2 mt-4"
            v-if="item.stat1"
          >
            <p class="text-xl font-medium font-poppins text-[#111827]">
              {{ item.stat1 }}
            </p>
          </div> -->
          <div class="flex flex-row justify-between mt-4">
            <div>
              <p class="font-medium font-poppins text-base text-[#111827]">
                {{ item.name }}
              </p>
            </div>
            <!-- <div class="text-sm" v-if="item.action">
              <a
                @click.prevent="item.action"
                href=""
                class="text-primary underline font-medium text-base font-poppins cursor-pointer"
                >View payments<span class="sr-only">
                  {{ item.name }} stats</span
                ></a
              >
            </div> -->
          </div>
          <div
            class="flex flex-row justify-start items-center mt-1"
            v-if="item.name === 'Refunds'"
          >
            <InformationCircleIcon class="w-5 h-5 text-gray-400" />
            <p class="text-gray-400 text-xs">
              Historical refunds may not be included
            </p>
          </div>
        </div>
      </dl>
    </div>
    <div class="sm:flex sm:items-center mb-6">
      <div
        class="sm:flex-auto flex flex-row justify-start space-x-2 items-center"
      >
        <h1 class="text-[#111827] font-poppins font-semibold text-2xl">
          Payments
        </h1>
        <arrow-path-icon
          class="w-6 h-6 text-black block cursor-pointer"
          @click="currentStore && refresh(currentStore)"
        />
        <json-csv
          :data="downloadable"
          v-if="downloadable && currentStore"
          :name="`${currentStore.data.description}-payments-${currentYear}-${currentMonth}`"
        >
          <arrow-down-tray-icon
            class="w-6 h-6 text-black block cursor-pointer"
          />
        </json-csv>
      </div>
    </div>
    <div class="flex flex-row justify-between items-center mb-6">
      <ChevronLeftIcon
        class="h-16 w-16 text-black cursor-pointer"
        aria-hidden="true"
        @click="previousMonth"
      />
      <div class="text-xl flex flex-row justify-start items-center space-x-2">
        <custom-select
          v-model="currentYear"
          :options="years"
          class="w-[150px]"
        />
        <div class="h-[20px]"><p>-</p></div>
        <custom-select
          v-model="selectedMonth"
          :options="months"
          class="w-[150px]"
        />
      </div>
      <ChevronRightIcon
        class="h-16 w-16 text-black cursor-pointer"
        aria-hidden="true"
        @click="nextMonth"
      />
    </div>
    <div
      class="flex flex-col justify-start items-center"
      v-if="!paymentSummaries || paymentSummaries.length === 0"
    >
      <h1
        class="font-poppins text-xl font-semibold leading-6 text-gray-900 mt-6"
      >
        No payments for the period selected.
      </h1>
    </div>
    <div v-else class="p-4 bg-white rounded-[16px]">
      <div class="-mx-4 mt-4 sm:-mx-0">
        <table class="min-w-full">
          <thead class="border-none">
            <tr class="border-none">
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Date
              </th>
              <th
                scope="col"
                class="rounded-l-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Total
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Fees
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Refunds
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Chargebacks
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Deductions
              </th>
              <th
                v-if="SHOW_FRANCHISE_FEE"
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Franchise fee
              </th>
              <th
                scope="col"
                class="hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                Net
              </th>

              <th
                scope="col"
                class="rounded-r-[16px] hidden sm:table-cell table-header font-poppins text-[12px] font-medium text-[#687588]"
              >
                <span class="sr-only">Details</span>
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            <tr
              v-for="payment in paymentSummaries.sort((p1, p2) =>
                p1.date > p2.date ? -1 : 1,
              )"
              :key="payment.id"
            >
              <td
                class="w-full max-w-0 py-4 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pl-0"
              >
                {{ payment.date }}
                <dl class="font-normal lg:hidden">
                  <dt class="sr-only">Total</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Total:{{
                      ((payment.total ?? 0) / 100).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}
                  </dd>
                  <dt class="sr-only">Fees</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Fees:{{
                      ((payment.commission ?? 0) / 100).toLocaleString(
                        "en-AU",
                        {
                          style: "currency",
                          currency: "AUD",
                        },
                      )
                    }}
                  </dd>
                  <dt class="sr-only">Refunds</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Refunds:{{
                      (PaymentSummary.refund(payment) / 100).toLocaleString(
                        "en-AU",
                        {
                          style: "currency",
                          currency: "AUD",
                        },
                      )
                    }}
                  </dd>
                  <dt class="sr-only">Chargebacks</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Chargebacks:{{
                      (
                        PaymentSummary.chargebackTotal(payment) / 100
                      ).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}
                  </dd>
                  <dt class="sr-only">Deductions</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Deductions:{{ money(PaymentSummary.deductions(payment)) }}
                  </dd>
                  <dt class="sr-only">Net</dt>
                  <dd class="mt-1 truncate text-gray-700">
                    Net:{{
                      (PaymentSummary.net(payment) / 100).toLocaleString(
                        "en-AU",
                        {
                          style: "currency",
                          currency: "AUD",
                        },
                      )
                    }}
                  </dd>
                </dl>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
                {{
                  ((payment.total ?? 0) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  ((payment.commission ?? 0) / 100).toLocaleString("en-AU", {
                    style: "currency",
                    currency: "AUD",
                  })
                }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <router-link
                  :to="{
                    name: 'store-refunds',
                    params: {
                      date: payment.date,
                      merchant: currentStore.merchant,
                      store: currentStore.store,
                    },
                  }"
                  v-if="currentStore"
                >
                  <a href="#" class="text-primary">
                    {{
                      (
                        (PaymentSummary.refund(payment) ?? 0) / 100
                      ).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}</a
                  >
                </router-link>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <router-link
                  :to="{
                    name: 'store-chargebacks',
                    params: {
                      date: payment.date,
                      merchant: currentStore.merchant,
                      store: currentStore.store,
                    },
                  }"
                  v-if="currentStore"
                >
                  <a href="#" class="text-primary">
                    {{
                      (
                        (PaymentSummary.chargebackTotal(payment) ?? 0) / 100
                      ).toLocaleString("en-AU", {
                        style: "currency",
                        currency: "AUD",
                      })
                    }}</a
                  >
                </router-link>
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                <a
                  href="#"
                  class="text-primary"
                  @click.prevent="openDeductions(payment.date)"
                >
                  {{ money(PaymentSummary.deductions(payment)) }}
                </a>
              </td>
              <td
                v-if="SHOW_FRANCHISE_FEE"
                class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell"
              >
                {{ money(PaymentSummary.franchiseFee(payment)) }}
              </td>
              <td class="hidden px-3 py-4 text-sm text-gray-500 sm:table-cell">
                {{
                  ((PaymentSummary.net(payment) ?? 0) / 100).toLocaleString(
                    "en-AU",
                    {
                      style: "currency",
                      currency: "AUD",
                    },
                  )
                }}
              </td>

              <td class="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-0">
                <router-link
                  :to="{
                    name: 'store-payments',
                    params: { date: payment.date },
                  }"
                >
                  <a href="#" class="text-primary hover:text-primary"
                    >Details<span class="sr-only">, {{ payment.id }}</span></a
                  >
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Deductions popup -->
    <div
      v-if="viewingDeductions"
      class="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/50 px-4"
    >
      <div class="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-[#111827]">
            Deductions — {{ viewingDeductions.date }}
          </h2>
          <button
            class="text-gray-400 hover:text-gray-600"
            @click="viewingDeductions = null"
          >
            ✕
          </button>
        </div>

        <div
          v-if="viewingDeductions.items.length === 0"
          class="text-sm text-gray-500"
        >
          No deductions for this day.
        </div>
        <table v-else class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="text-left text-xs uppercase text-gray-500">
            <tr>
              <th class="py-2 pr-3">Type</th>
              <th class="py-2 pr-3">Description</th>
              <th class="py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(d, i) in viewingDeductions.items" :key="i">
              <td class="py-2 pr-3 capitalize">{{ d.type }}</td>
              <td class="py-2 pr-3">{{ d.description }}</td>
              <td class="py-2 text-right">{{ money(d.amount?.amount ?? 0) }}</td>
            </tr>
          </tbody>
        </table>

        <div class="mt-5 flex justify-end">
          <button
            class="rounded-md px-4 py-2 text-sm text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            @click="viewingDeductions = null"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
